/* eslint-disable no-unused-vars */

import { getEstimatedWidthOfDomSpan } from '@/services/utils/modules/base'
import { isFunction, isString } from 'xe-utils'

// 根据dom来判断宽度, 如果差值超阈值, 就认为是dom还没有完全渲染, nextTick再次计算
const AUTO_WIDTH_DOM_DIFF_THRESHOLD_RENDER_NOT_COMPLETE = 500

function getPxNum(px) {
  const num = String(px).endsWith('px') ? String(px).slice(0, -2) : px
  return Math.ceil(Number(num))
}

const getDomsMaxScrollWidth = (doms) => {
  let maxOffsetWidth = 0
  let maxScrollWidth = 0
  doms.forEach(dom => {
    const scrollWidth = dom.scrollWidth || 0
    const offsetWidth = dom.offsetWidth || 0
    if (scrollWidth > maxScrollWidth) {
      maxOffsetWidth = offsetWidth
      maxScrollWidth = scrollWidth
    }
  })

  return {
    maxOffsetWidth,
    maxScrollWidth,
  }
}

export const getColClassName = ({ row, rowIndex, $rowIndex, column, columnIndex, $columnIndex }) => {
  // 第一个class 用于调整样式，让vxe-cell 宽度突破父元素的限制
  // 第二个class 是用于标识该列
  return `p__vxe-table__body__col__auto-adjustment-width p__vxe-td__field__${column.field}`
}

export const transColumnsFixClassName = (columns) => {
  // 利用了对象ref特性，深层次的函数修改对象，调用者这边也会更改
  const localColumns = columns
  if (!localColumns) {
    return []
  }

  localColumns.forEach(col => {
    // 这里仅仅处理最后一一层
    if (col.children) {
      transColumnFixClassName(col.children)
      return
    }

    transColumnFixClassName(col)
  })

  return localColumns
}

export const transColumnFixClassName = (col) => {
  const { autoWidth = false, className = '', footerClassName = '' } = col
  if (!autoWidth) {
    return
  }

  // vxe table col className 包装器
  // 内容区域
  const getClassNameWrapper = (...args) => {
    const appendClassName = getColClassName(...args)

    let originalClassName = ''
    if (isString(className)) {
      originalClassName = className
    } else if (isFunction(className)) {
      originalClassName = className(...args)
    }

    return `${originalClassName} ${appendClassName}`
  }

  // 封装 footerClassName
  const getFooterClassNameWrapper = (...args) => {
    const appendClassName = getColClassName(...args)

    let originalClassName = ''
    if (isString(footerClassName)) {
      originalClassName = footerClassName
    } else if (isFunction(footerClassName)) {
      originalClassName = footerClassName(...args)
    }

    return `${originalClassName} ${appendClassName}`
  }

  // 修改className
  col.className = getClassNameWrapper
  col.footerClassName = getFooterClassNameWrapper
  col.showOverflow = false

  return col
}

const getVxeCellSelectorByField = (field) => {
  return `.p__vxe-table__body__col__auto-adjustment-width.p__vxe-td__field__${field} .vxe-cell`
}

const getDomListOfVxeCell = (vxeGridRef, field) => {
  const selector = getVxeCellSelectorByField(field)
  const doms = vxeGridRef.$el.querySelectorAll(selector)
  return doms
}

const pxCompare = (aPx, bPx) => {
  const aNum = getPxNum(aPx)
  const bNum = getPxNum(bPx)

  if (aNum === bNum) {
    return 0
  }

  return aNum > bNum ? 1 : -1
}

export const getAutoWidthFieldList = (columns) => {
  const fieldListAutoWidth = []
  columns.forEach(col => {
    const { field, autoWidth, children } = col
    if (children) {
      const childrenFiledList = getAutoWidthFieldList(children)
      fieldListAutoWidth.push(...childrenFiledList)
      return
    }

    if (autoWidth === true) {
      fieldListAutoWidth.push(field)
    }
  })

  return fieldListAutoWidth
}

/**
 * 获取列最大宽度，通过dom获取
 * @param {*} vxeGridRef
 * @param {*} field
 * @returns
 */
const getColMaxWidthByDom = (vxeGridRef, field) => {
  const vxeCellDomList = getDomListOfVxeCell(vxeGridRef, field)
  const domMaxWidth = getDomsMaxScrollWidth(vxeCellDomList)
  return domMaxWidth
}

/**
 * 获取列最大宽度，通过预估数据的宽度
 * @param {*} vxeGridRef
 * @param {*} field
 * @returns
 */
const getColMaxWidthByTableDataDefault = (tableData, colInner = {}) => {
  let maxWidth = 0

  const { field } = colInner

  tableData.forEach(row => {
    const fieldValue = row[field] || ''
    if (!fieldValue) {
      return
    }

    const curWidth = getEstimatedWidthOfDomSpan(fieldValue)
    if (curWidth > maxWidth) {
      maxWidth = curWidth
    }
  })

  const finalWidth = Math.ceil(maxWidth) + 24

  return finalWidth
}

/**
 * 把columns 所有叶子节点放到一个对象里面来
 * @param {*} columns
 * @returns
 */
const getColumnsLeafNodeObj = (columnsProp = []) => {
  const ret = {}

  columnsProp.forEach(col => {
    if (col.children) {
      const childrenLeafNodeObj = getColumnsLeafNodeObj(col.children)
      Object.assign(ret, childrenLeafNodeObj)
      return
    }

    const { field } = col
    if (field) {
      ret[field] = col
    }
  })

  return ret
}

const reComputeColumnsAutoWidth = (vxeGridRef, columnsProp = [], tableData = [], columns = [], columnsPropLeafNodeObj = {}, options = {}) => {
  let isNeedReloadColumn = false
  let isNeedRecomputeNextTick = false

  const { isOnlyAutoWidthDom } = options
  if (isOnlyAutoWidthDom) {
    console.log('本次自动宽度, 仅仅需要处理autoWidthDom类型的列')
  }

  columns.forEach(column => {
    const { field, minWidth, width, children } = column
    if (children) {
      // 有下一级标题
      const isChildrenNeedReload = reComputeColumnsAutoWidth(vxeGridRef, columnsProp, tableData, children, columnsPropLeafNodeObj)
      if (isChildrenNeedReload) {
        isNeedReloadColumn = isChildrenNeedReload
      }
      return
    }

    const colProp = columnsPropLeafNodeObj[field]
    if (!colProp) {
      return
    }

    const { hcPreset } = colProp
    const { autoWidth, autoWidthDom, footerAutoWidthDom, getMaxColWidth = getColMaxWidthByTableDataDefault, autoWidthGap = 4 } = hcPreset || {}

    if (autoWidth !== true) {
      return
    }

    // 处理宽度
    const curWidth = width || minWidth
    let domMaxWidthNumFinal = curWidth

    if (autoWidthDom) {
      // 根据渲染后的dom来判断最长的宽度(兼容性好), 性能差, 虚拟滚动的时候, 每次滚动还需要再次计算
      // 自带 footer 自动宽度, 根据dom来计算
      const { maxOffsetWidth, maxScrollWidth } = getColMaxWidthByDom(vxeGridRef, field)
      if (maxScrollWidth <= maxOffsetWidth) {
        console.log(`field: ${field} maxScrollWidth 不大于 maxOffsetWidth, 当前列不需要处理`)
        return
      }

      const domMaxWidthNum = getPxNum(maxScrollWidth)
      domMaxWidthNumFinal = domMaxWidthNum + 12 + autoWidthGap
      // domMaxWidthNumFinal = domMaxWidthNum
      // 根据dom来判断宽度, 如果差值超过阈值, 就认为是dom还没有完全渲染, nextTick再次计算
      if (getPxNum(domMaxWidthNumFinal) - getPxNum(curWidth) > AUTO_WIDTH_DOM_DIFF_THRESHOLD_RENDER_NOT_COMPLETE) {
        console.log(`===reComputeColumnsAutoWidth field: ${field} curWidth: ${curWidth} domMaxWidthNumFinal: ${domMaxWidthNumFinal} 最大宽度和当前宽度差额超过 ${AUTO_WIDTH_DOM_DIFF_THRESHOLD_RENDER_NOT_COMPLETE}, 应该是虚拟渲染导致还没有渲染完成, 下个tick再来计算自动宽度`)
        isNeedRecomputeNextTick = true
        return
      }
    } else if (autoWidth) {
      // autoWidth
      // 预估预设算法预估宽度, 兼容性差, 但是性能好

      // bad design  autoWidthDom === autoWidth + footerAutoWidthDom 有点混淆
      // autoWidth 场景下, 也有可能呢有 footerAutoWidthDom

      let footerDomMaxWidth = 0
      // 列配置了 footerAutoWidthDom 且 当前grid 显示了 footer, 计算当前列 footer 的最大宽度
      if (footerAutoWidthDom && vxeGridRef.showFooter) {
        const { maxOffsetWidth, maxScrollWidth } = getColMaxWidthByDom(vxeGridRef, field)
        if (maxScrollWidth <= maxOffsetWidth) {
          console.log(`footerAutoWidthDom --> field: ${field} maxScrollWidth 不大于 maxOffsetWidth, 当前列不需要处理`)
        } else {
          // footer 没有显示完内容
          const footerDomMaxWidthActual = getPxNum(maxScrollWidth) + 12 + autoWidthGap
          // 根据dom来判断宽度, 如果差值超过阈值, 就认为是dom还没有完全渲染, nextTick再次计算
          if (getPxNum(footerDomMaxWidthActual) - getPxNum(curWidth) > AUTO_WIDTH_DOM_DIFF_THRESHOLD_RENDER_NOT_COMPLETE) {
            console.log(`footerAutoWidthDom --> reComputeColumnsAutoWidth field: ${field} curWidth: ${curWidth} footerDomMaxWidthActual: ${footerDomMaxWidthActual} 最大宽度和当前宽度差额超过 ${AUTO_WIDTH_DOM_DIFF_THRESHOLD_RENDER_NOT_COMPLETE}, 应该是虚拟渲染导致还没有渲染完成, 下个tick再来计算自动宽度`)
            isNeedRecomputeNextTick = true
          } else {
            footerDomMaxWidth = footerDomMaxWidthActual
          }
        }
      }

      // 处理body里面, 预估最大宽度逻辑
      let bodyEstimateMaxWidth = 0
      if (!isOnlyAutoWidthDom) {
        // 还需要处理 预估宽度逻辑
        // 根据tabelData 预估最长宽度(推荐)
        const domMaxWidth = getMaxColWidth(tableData, colProp) // 表格左右有12px padding
        const domMaxWidthNum = getPxNum(domMaxWidth)

        // 最终的宽度要考虑表格左右  padding: 0 12px  == 24
        // 8 预留空间
        // autoWidthGap 列指定的额外空隙
        bodyEstimateMaxWidth = domMaxWidthNum + 24 + 8 + autoWidthGap
      }

      // 结合body footer 判断col max width
      domMaxWidthNumFinal = Math.max(getPxNum(footerDomMaxWidth), getPxNum(bodyEstimateMaxWidth))
    }

    if (pxCompare(domMaxWidthNumFinal, curWidth) < 1) {
      // 该列最大宽度，没有达到设置的 minWidth,不需要处理
      return
    }

    console.log(`===field: ${field} autoWidthDom: ${autoWidthDom} footerAutoWidthDom: ${footerAutoWidthDom} ==> curWidth: ${curWidth} domMaxWidthNumFinal: ${domMaxWidthNumFinal}`)
    // 需要处理
    isNeedReloadColumn = true
    column.width = domMaxWidthNumFinal
  })

  return {
    isNeedReloadColumn,
    isNeedRecomputeNextTick,
  }
}

/**
 * 重新计算 table 宽度
 * @param {*} vxeGridRef tableRef
 * @param {*} columnsProp 最终给vxe-grid 的columns
 * @param {*} tableData 当前表格数据
 * @returns
 */
export const reComputeAutoWidth = (vxeGridRef, columnsProp = [], tableData, options = {}) => {
  // 转换成obj,方便取值
  const columnsPropLeafNodeObj = getColumnsLeafNodeObj(columnsProp)

  const isExistAutoWidthCol = Object.values(columnsPropLeafNodeObj).find(col => {
    const { hcPreset } = col
    const { autoWidth } = hcPreset || {}
    return autoWidth === true
  })
  if (!isExistAutoWidthCol) {
    // 没有需要自适应宽度的列
    return
  }

  const tableRef = vxeGridRef
  const columns = tableRef.getColumns()
  // console.log('tableRef', tableRef)
  // console.log('columns:', columns)
  // columns[0].minWidth = '100px'

  const { isNeedReloadColumn, isNeedRecomputeNextTick } = reComputeColumnsAutoWidth(vxeGridRef, columnsProp, tableData, columns, columnsPropLeafNodeObj, options)

  if (isNeedReloadColumn) {
    console.log('自动宽度: 需要调整宽度，开始 loadColumn(columns)')
    console.log('===columns', JSON.parse(JSON.stringify(columns)))
    // 动态更新了 columns, 直接relresh, 不能直接tableRef.loadColumn(columns)，有二级表头的场景会失效
    // tableRef.loadColumn(columns)
    tableRef.refreshColumn()
  } else {
    console.log('自动宽度: 不需要调整宽度')
  }

  return {
    isNeedRecomputeNextTick
  }
}
